import { useCallback, useMemo } from 'react';
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor, type LayoutItem, type Layout } from 'react-grid-layout';
import { useDashboard } from '../../contexts/DashboardContext';
import { getWidgetDefinition } from './widgets/widgetRegistry';
import WidgetWrapper from './widgets/WidgetWrapper';
import type { WidgetLayout } from '../../types/dashboard';

import 'react-grid-layout/css/styles.css';

const ROW_HEIGHT = 60;
const COLS = { lg: 12, md: 10, sm: 6, xs: 4 };
const BREAKPOINTS = { lg: 1400, md: 1024, sm: 768, xs: 0 };

const WidgetGrid = () => {
  const { layout, editMode, updateLayout, removeWidget } = useDashboard();
  const { width, mounted, containerRef } = useContainerWidth();

  const gridLayouts = useMemo(() => {
    const lgLayout: LayoutItem[] = layout.widgets.map((w) => {
      const def = getWidgetDefinition(w.type);
      return {
        i: w.i,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        minW: def?.minSize.w || 2,
        minH: def?.minSize.h || 2,
        maxW: 12, // Limita largura máxima
        static: !editMode,
      };
    });

    // Criar layouts responsivos para diferentes breakpoints
    const mdLayout: LayoutItem[] = layout.widgets.map((w) => {
      const def = getWidgetDefinition(w.type);
      // Ajustar largura proporcionalmente para telas médias (10 colunas)
      const adjustedW = Math.min(Math.ceil(w.w * 10 / 12), 10);
      return {
        i: w.i,
        x: w.x % 10, // Ajustar posição x para caber em 10 colunas
        y: w.y,
        w: adjustedW,
        h: w.h,
        minW: Math.min(def?.minSize.w || 2, 5),
        minH: def?.minSize.h || 2,
        maxW: 10,
        static: !editMode,
      };
    });

    const smLayout: LayoutItem[] = layout.widgets.map((w) => {
      const def = getWidgetDefinition(w.type);
      // Ajustar largura proporcionalmente para telas pequenas (6 colunas)
      const adjustedW = Math.min(Math.ceil(w.w * 6 / 12), 6);
      return {
        i: w.i,
        x: 0, // Força alinhamento à esquerda em telas pequenas
        y: w.y,
        w: adjustedW,
        h: w.h,
        minW: Math.min(def?.minSize.w || 2, 3),
        minH: def?.minSize.h || 2,
        maxW: 6,
        static: !editMode,
      };
    });

    const xsLayout: LayoutItem[] = layout.widgets.map((w) => {
      const def = getWidgetDefinition(w.type);
      // Em telas muito pequenas, ocupar toda a largura
      return {
        i: w.i,
        x: 0,
        y: w.y,
        w: 4, // Largura total em xs
        h: w.h,
        minW: 2,
        minH: def?.minSize.h || 2,
        maxW: 4,
        static: !editMode,
      };
    });

    return {
      lg: lgLayout,
      md: mdLayout,
      sm: smLayout,
      xs: xsLayout
    };
  }, [layout.widgets, editMode]);

  const onLayoutChange = useCallback((currentLayout: Layout) => {
    if (!editMode) return;
    const updated: WidgetLayout[] = layout.widgets.map((w) => {
      const gridItem = currentLayout.find((l) => l.i === w.i);
      if (!gridItem) return w;
      return { ...w, x: gridItem.x, y: gridItem.y, w: gridItem.w, h: gridItem.h };
    });
    updateLayout(updated);
  }, [editMode, layout.widgets, updateLayout]);

  if (layout.widgets.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: 'var(--space-12)',
        color: 'var(--text-tertiary)', fontSize: 'var(--text-base)',
      }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: 'var(--space-4)' }}>📊</span>
        Nenhum widget adicionado.<br />
        Clique em &quot;Adicionar Widget&quot; para personalizar sua dashboard.
      </div>
    );
  }

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '0 var(--space-4)',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {mounted && (
        <ResponsiveGridLayout
          className="widget-grid"
          layouts={gridLayouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          width={width}
          dragConfig={{ enabled: editMode, handle: '.widget-drag-handle', threshold: 3, bounded: true }}
          resizeConfig={{ enabled: editMode, handles: ['se'] }}
          rowHeight={ROW_HEIGHT}
          margin={[16, 16] as const}
          containerPadding={[0, 0] as const}
          onLayoutChange={onLayoutChange}
        >
          {layout.widgets.map((widget) => (
            <div key={widget.i}>
              <WidgetWrapper
                widgetType={widget.type}
                config={widget.config}
                isEditMode={editMode}
                onRemove={() => removeWidget(widget.i)}
                width={widget.w * 100}
                height={widget.h * ROW_HEIGHT}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
};

export default WidgetGrid;
